/**
 * Frontend TypeScript types for AWS Transcription feature
 * Mirrors server/types/transcription.ts for type safety across frontend/backend
 */

// ============================================================================
// Job Status Enum
// ============================================================================

export enum TranscriptionJobStatus {
  PENDING = 'pending',
  PROCESSING = 'processing',
  COMPLETED = 'completed',
  FAILED = 'failed',
  ARCHIVED = 'archived'
}

// ============================================================================
// API Response Types
// ============================================================================

export interface TranscriptionJob {
  id: string;
  requestId: number;
  fileName: string;
  fileType: string;
  fileSize?: number;
  status: TranscriptionJobStatus;
  errorMessage: string | null;
  errorCode: string | null;
  createdAt: string;              // ISO timestamp
  startedAt: string | null;
  completedAt: string | null;
}

export interface CreateRequestWithImportResponse {
  requestId: number;
  jobId: string;
  message: string;
  estimatedCompletionTime: string; // ISO timestamp
}

export interface JobStatusResponse {
  requestId: number;
  jobId: string;
  status: TranscriptionJobStatus;
  fileName: string;
  fileType?: string; // 'pdf', 'image', 'audio'
  errorMessage?: string;
  errorCode?: string;
  createdAt: string;
  completedAt?: string;
}

// ============================================================================
// Request Creation Types (Frontend)
// ============================================================================

export interface CreateRequestWithImportData {
  customerName: string;
  region: string;
  requestorName: string;
  requestorEmail: string;
  file: {
    uri: string;
    name: string;
    type: string;
    size: number;
    webFile?: File; // Store actual File object for web platform
  };
}

/**
 * Data for transcribing an existing request (audio recording on RequestDetail)
 */
export interface TranscribeExistingRequestData {
  requestId: number;
  file: {
    uri: string;
    name: string;
    type: string;
    size: number;
    webFile?: File; // Store actual File object for web platform
  };
}

// ============================================================================
// Error Types
// ============================================================================

export enum TranscriptionErrorCode {
  // File validation errors
  FILE_TOO_LARGE = 'FILE_TOO_LARGE',
  INVALID_FILE_TYPE = 'INVALID_FILE_TYPE',
  FILE_CORRUPTED = 'FILE_CORRUPTED',

  // AWS errors
  AWS_TEXTRACT_FAILED = 'AWS_TEXTRACT_FAILED',
  AWS_TRANSCRIBE_FAILED = 'AWS_TRANSCRIBE_FAILED',
  AWS_BEDROCK_FAILED = 'AWS_BEDROCK_FAILED',
  AWS_RATE_LIMIT = 'AWS_RATE_LIMIT',
  AWS_TIMEOUT = 'AWS_TIMEOUT',

  // Processing errors
  PROCESSING_TIMEOUT = 'PROCESSING_TIMEOUT',
  FFMPEG_CONVERSION_FAILED = 'FFMPEG_CONVERSION_FAILED',
  EXTRACTION_FAILED = 'EXTRACTION_FAILED',

  // Database errors
  REQUEST_NOT_FOUND = 'REQUEST_NOT_FOUND',
  JOB_NOT_FOUND = 'JOB_NOT_FOUND',
  DATABASE_ERROR = 'DATABASE_ERROR',

  // Validation errors
  VALIDATION_ERROR = 'VALIDATION_ERROR',
  UNAUTHORIZED = 'UNAUTHORIZED'
}

export interface TranscriptionError {
  code: TranscriptionErrorCode;
  message: string;
  details?: any;
}

// ============================================================================
// UI Component Props Types
// ============================================================================

export interface SplitButtonProps {
  onDefaultAction: () => void;
  onDropdownAction: () => void;
  defaultLabel: string;
  dropdownLabel: string;
  disabled?: boolean;
  hasPermission: boolean;
}

export interface ImportPreCreationModalProps {
  visible: boolean;
  onClose: () => void;
  onSubmit: (data: CreateRequestWithImportData) => void;
  loading?: boolean;
  error?: string | null;
}

export interface ProcessingModalProps {
  visible: boolean;
  onClose: () => void;
  requestId?: number;
  fileName?: string;
}

export interface ProcessingBadgeProps {
  stage: string;
  size?: 'small' | 'medium' | 'large';
}
